import { useCallback, useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE } from '../../../i18n/constants';
import { useFlag } from '../../../services/launchDarkly';
import type { Category } from '../../../stores/categories/CategoriesStore';
import { useStores } from '../../../stores/useStores';
import { prefersReducedMotion } from '../../../utils/prefersReducedMotion';
import Chevron from '../../SVGIcons/Chevron/Chevron';
import {
  CAROUSEL_AUTO_LOAD_MAX_GAMES,
  CAROUSEL_CARD_TRAVEL_MS,
  CAROUSEL_COMPACT_BELOW_GAMES,
  CAROUSEL_COMPACT_FADE_STEPS,
  CAROUSEL_COMPACT_VISIBLE_STEPS,
  CAROUSEL_DIM_STOPS,
  CAROUSEL_LOAD_AHEAD_CARDS,
  CAROUSEL_LOAD_RETRY_MS,
  CAROUSEL_RESUME_DELAY_MS,
  CAROUSEL_OFFSET_STOPS,
  CAROUSEL_SCALE_STOPS,
  CAROUSEL_STEP_MS,
  CAROUSEL_STRIDE_RATIO,
} from '../constants';
import { GameCard } from '../GameCard';
import carouselStyles from '../GamesCarousel.module.scss';
import { seenGamesLobbyListener } from '../seenGamesImpression';

import { isNearEnd, needsNextPage, wrappedDelta } from './carouselLoadMore';
import styles from './GamesAutoCarousel.module.scss';

/** Symmetric ease so a step leaves and arrives gently rather than starting at full speed. */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * `stops` read at `distance` card-steps from the centre, interpolated between whole-card stops so a
 * card changes smoothly as it travels rather than stepping at each boundary. Past the last stop it
 * holds, or with `extrapolate` carries on at the last segment's slope.
 */
function valueAt(stops: number[], distance: number, extrapolate = false): number {
  const last = stops.length - 1;
  if (distance >= last) return stops[last] + (extrapolate ? (stops[last] - stops[last - 1]) * (distance - last) : 0);

  const lower = Math.floor(distance);
  return stops[lower] + (stops[lower + 1] - stops[lower]) * (distance - lower);
}

/** The slowest the painted row spreads, as a share of the layout stride — sets how far `paint` must reach. */
const MIN_SPREAD =
  (CAROUSEL_OFFSET_STOPS[CAROUSEL_OFFSET_STOPS.length - 1] - CAROUSEL_OFFSET_STOPS[CAROUSEL_OFFSET_STOPS.length - 2]) /
  CAROUSEL_STRIDE_RATIO;

/**
 * Wrapping inside the band [0, 2W) needs 2W of runway behind the viewport — `copies * W - viewport
 * >= 2W` — so the count is `ceil(viewport / W) + 2`, and three is what that bottoms out at. Named
 * so the initial state can start there rather than below it, which would cost the layout effect a
 * guaranteed tear-down-and-rerun on mount.
 */
const MIN_CAROUSEL_COPIES = 3;

/** Strides past the viewport edge that still get their scale and dim written, so a card is styled before it scrolls in. */
const PAINT_MARGIN_STRIDES = 3;

/** Chrome focuses an `<a>` on a mouse click too; only keyboard focus should hold the drift. */
function isKeyboardFocus(el: EventTarget): boolean {
  try {
    return (el as Element).matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * The copy that stands for the row: the one it parks on at mount, the one carrying the onboarding
 * highlight and its tooltip anchor, and the only one exposed to screen readers and the tab order.
 *
 * It has to be this one rather than the literal first copy. Copy 0 spans [0, W), which is flush
 * against the bottom of the wrap band — bringing one of its cards into view drives scrollLeft to 0,
 * `wrapScroll` shifts the row a whole copy to get off the boundary, and whatever the browser was
 * scrolling to is a copy-width away again. Copy 1 spans [W, 2W), strictly inside the band, so every
 * card in it can be scrolled to without tripping a wrap.
 */
const PARKED_COPY = 1;

/**
 * A category rendered as a continuously auto-scrolling, infinitely looping row (PLT-795).
 *
 * The loop is a marquee. The games are laid out `copies` times and the scroll position wraps by
 * exactly one copy's width — since position `x` and `x + copyWidth` show the same thing, the wrap
 * is invisible and the row never hits a dead stop in either direction.
 *
 * `copies` is measured rather than fixed. Wrapping inside the band [0, 2W) needs 2W of scrollable
 * runway, i.e. `copies * W - viewport >= 2W`, so a category whose games are narrower than the
 * viewport needs more than the two copies a full-width row would. Resting at W (one copy in) rather
 * than 0 is what gives a swipe somewhere to go leftwards: at a true scrollLeft of 0 the browser
 * clamps the gesture and fires no scroll event, so nothing would ever wrap it round to the end.
 *
 * Motion is a constant drift — smooth and continuously moving, never resting on a card. Whichever
 * card is nearest the middle reads as the focal one, and the scale and dim curves are continuous,
 * so a card grows and brightens as it travels in rather than snapping between states.
 *
 * It runs off rAF rather than a CSS animation because the player has to be able to take over
 * mid-flight: a drag, a swipe or an arrow click parks it for CAROUSEL_RESUME_DELAY_MS, and an
 * arrow eases exactly one card before the drift picks up again from there.
 *
 * On top of that the row is a coverflow: a card scales and dims by how far it sits from the middle
 * of the viewport, so whatever is centred reads as the focal card. Cards are laid out a fixed
 * stride apart and scaled in place, which is what lets the loop arithmetic stay in layout pixels —
 * `transform` is painted, never measured. The design packs its outer cards very slightly tighter
 * than a fixed stride does (~25px by the third card out), which is invisible next to the scaling.
 *
 * Per-frame work writes CSS custom properties straight onto the cached card elements. Going through
 * React state here would re-render the whole row 60 times a second.
 */
export const GamesAutoCarousel = observer(function GamesAutoCarousel({
  id,
  onboardingRow,
  setReferenceElement,
  carouselClassName,
  carouselLeftArrowClassName,
  carouselRightArrowClassName,
}: {
  id: Category['masterGameSubCategoryId'];
  onboardingRow: boolean;
  setReferenceElement: Dispatch<SetStateAction<HTMLElement | null>>;
  carouselClassName?: string;
  carouselLeftArrowClassName?: string;
  carouselRightArrowClassName?: string;
}) {
  const { t } = useTranslation();
  const { categoriesStore, coinTypeStore } = useStores();
  const category = categoriesStore.categoriesRegistry[id];

  const carouselRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  // Timestamp (performance.now) before which the animation stays parked. 0 = running.
  const pausedUntil = useRef(0);
  const copyWidth = useRef(0);
  // Set by every write the carousel makes to scrollLeft, cleared by the scroll event it causes, so
  // the handler can tell the row moving itself apart from the player moving it.
  const selfScroll = useRef(false);
  // The opening park happens once and only once — see `measure`.
  const hasParked = useRef(false);

  const [copies, setCopies] = useState(MIN_CAROUSEL_COPIES);
  // The drift is the only thing a row that nobody can see is doing; rows are mounted lazily but
  // never unmounted, so without this every carousel the player has scrolled past keeps painting
  // every one of its cards for the rest of the session.
  const [inView, setInView] = useState(true);
  // Holds the drift while a keyboard user is inside the row — see `holdDrift`.
  const [focusWithin, setFocusWithin] = useState(false);

  // Cards paired with their layout centre, cached so the per-frame paint neither queries the DOM
  // nor reads offsetLeft (which would force a layout on every tick).
  const items = useRef<{ el: HTMLElement; centre: number }[]>([]);
  const stride = useRef(0);
  // Non-null while a step is easing. The scroll handler checks it: the step drives scrollLeft
  // itself, and the scroll events that causes must not be treated as the player scrolling.
  const step = useRef<{ from: number; to: number; startedAt: number; duration: number } | null>(null);
  // The drift's own position, in unrounded pixels. The browser quantises scrollLeft to the device
  // pixel grid, so reading it back each frame would round a sub-pixel step away and the row would
  // sit still — at five seconds a card that step is ~0.26px against a 0.5px grid.
  const offset = useRef(0);

  // How far the row has moved since the list last grew, wraps not counted — see `needsNextPage`.
  const travelled = useRef(0);
  const lastCentre = useRef<number | null>(null);
  // The card nearest the middle and how far its centre sits from it, kept current by `paint` so
  // that when a page lands `measure` can put that same card back where the player was looking.
  const anchor = useRef<{ el: HTMLElement | null; delta: number }>({ el: null, delta: 0 });
  const measuredCount = useRef(0);
  const lastLoadAttempt = useRef(-Infinity);

  const games = category?.subCategoryGames;
  const gameCount = games?.length ?? 0;

  const gameLabelFF = useFlag('game_labels_feat', true);

  const pauseAutoScroll = useCallback(() => {
    pausedUntil.current = performance.now() + CAROUSEL_RESUME_DELAY_MS;
  }, []);

  /**
   * A gesture takes the row over from whatever it was doing.
   *
   * An arrow step in flight is the row driving itself, and the tick applies it before it consults
   * the pause — so without dropping the step here, a swipe begun inside the 600ms of an
   * arrow animation would have `scrollLeft` overwritten frame by frame instead of taking control.
   * `nudge` deliberately pauses without going through this, so tapping an arrow twice quickly still
   * chains off the step already in flight rather than cancelling it.
   */
  const handleGesture = useCallback(() => {
    if (step.current) {
      step.current = null;
      // The drift and the next gesture both pick up from where the row actually is.
      offset.current = carouselRef.current?.scrollLeft ?? offset.current;
    }
    pauseAutoScroll();
  }, [pauseAutoScroll]);

  /**
   * Every write the carousel makes to `scrollLeft` goes through here, flagged as its own.
   *
   * `seenGamesLobbyListener` is debounced by two seconds of stillness, and it reports every visible
   * game in EVERY visible row, not just this one. A row that moves on every frame never lets that
   * debounce reach its trailing edge, so feeding it the drift's scroll events would stop the whole
   * lobby reporting impressions for as long as a carousel is mounted — which, rows never being
   * unmounted, is the rest of the session.
   */
  const scrollTo = useCallback((left: number) => {
    const $el = carouselRef.current;
    if (!$el) return;

    const before = $el.scrollLeft;
    $el.scrollLeft = left;
    // Only once the write has actually landed. The browser quantises scrollLeft to the device pixel
    // grid and clamps it at both ends, so a frame of drift often resolves to where the row already
    // was — and no scroll event follows. Flagging that write would leave the flag standing until
    // some later event, and the one it swallowed would be the player's.
    if ($el.scrollLeft !== before) selfScroll.current = true;
  }, []);

  /** Scroll offset that centres `card`. */
  const offsetToCentre = useCallback((centre: number) => {
    const $el = carouselRef.current;
    return $el ? centre - $el.clientWidth / 2 : 0;
  }, []);

  /**
   * The card one step away in `direction`, or the nearest card when `direction` is 0.
   *
   * An arrow moves the row by a card, never by a pixel amount.
   */
  const targetCard = useCallback(
    (direction: -1 | 1, fromOffset?: number) => {
      const $el = carouselRef.current;
      if (!$el || !items.current.length || !stride.current) return null;

      const viewportCentre = (fromOffset ?? $el.scrollLeft) + $el.clientWidth / 2;

      if (direction === 1) {
        const next = items.current.find(({ centre }) => centre > viewportCentre + 1);
        return offsetToCentre(next ? next.centre : viewportCentre + stride.current);
      }

      const previous = items.current.filter(({ centre }) => centre < viewportCentre - 1).pop();
      return offsetToCentre(previous ? previous.centre : viewportCentre - stride.current);
    },
    [offsetToCentre],
  );

  /** Move one card in `direction`, taking the row over from whatever it was doing. */
  const nudge = useCallback(
    (direction: -1 | 1) => {
      const $el = carouselRef.current;
      if (!$el) return;

      pauseAutoScroll();

      // Aim from inside [W, 2W): near the seam the target could fall outside the scroll range, where the
      // browser clamps it and the wrap would then jump from the clamped spot instead of the intended one.
      const width = copyWidth.current;
      const inFlight = step.current;
      const heading = inFlight?.to ?? $el.scrollLeft;
      const shift = width > 0 ? (Math.floor(heading / width) - 1) * width : 0;
      if (shift) {
        scrollTo($el.scrollLeft - shift);
        if (inFlight) step.current = { ...inFlight, from: inFlight.from - shift, to: inFlight.to - shift };
      }

      // Chain off where a step in flight is headed, so tapping an arrow twice quickly moves two
      // cards instead of re-targeting the one already being approached.
      const to = targetCard(direction, step.current?.to ?? $el.scrollLeft);
      if (to !== null) {
        step.current = { from: $el.scrollLeft, to, startedAt: performance.now(), duration: CAROUSEL_STEP_MS };
      }
    },
    [pauseAutoScroll, scrollTo, targetCard],
  );

  const loadMore = useCallback(() => {
    const now = performance.now();
    // A request that fails leaves the row still wanting a page, and `paint` asks every frame.
    if (now - lastLoadAttempt.current < CAROUSEL_LOAD_RETRY_MS) return;
    if (!categoriesStore.categoriesRegistry[id]?.hasMoreGames || categoriesStore.loading.get('pagination')) return;

    lastLoadAttempt.current = now;
    void categoriesStore.getNextGames(id);
  }, [categoriesStore, id]);

  // Scale, dim and stack every card by its distance from the middle of the viewport.
  const paint = useCallback(
    (byPlayer = false) => {
      const $el = carouselRef.current;
      if (!$el || !stride.current) return;

      const viewportCentre = $el.scrollLeft + $el.clientWidth / 2;

      if (lastCentre.current !== null) {
        travelled.current += Math.abs(wrappedDelta(viewportCentre - lastCentre.current, copyWidth.current));
      }
      lastCentre.current = viewportCentre;

      let nearest = Infinity;
      let nearestIndex = 0;

      // Only the cards that are, or are about to be, on screen: centres are in layout order, and a
      // long category repeated across copies is hundreds of cards nobody can see.
      const list = items.current;
      // Painted cards sit closer in than their layout slot, so more of them are on screen than the layout width implies.
      const reach = $el.clientWidth / 2 / MIN_SPREAD + PAINT_MARGIN_STRIDES * stride.current;
      let first = 0;
      let end = list.length;
      while (first < end) {
        const mid = (first + end) >> 1;
        if (list[mid].centre < viewportCentre - reach) first = mid + 1;
        else end = mid;
      }

      for (let index = first; index < list.length && list[index].centre <= viewportCentre + reach; index++) {
        const { el, centre } = list[index];
        const distance = Math.abs(centre - viewportCentre) / stride.current;

        if (distance < nearest) {
          nearest = distance;
          nearestIndex = index;
          anchor.current.el = el;
          anchor.current.delta = centre - viewportCentre;
        }

        // The card is laid out `distance` strides from the middle and painted where the design has it.
        const cardWidth = stride.current / CAROUSEL_STRIDE_RATIO;
        const painted = valueAt(CAROUSEL_OFFSET_STOPS, distance, true) * cardWidth;
        const shift = Math.sign(centre - viewportCentre) * (painted - distance * stride.current);

        el.style.setProperty('--shift', `${shift}px`);
        el.style.setProperty('--scale', String(valueAt(CAROUSEL_SCALE_STOPS, distance)));
        el.style.setProperty('--dim', String(valueAt(CAROUSEL_DIM_STOPS, distance)));
        // Of a short list, only the focal card and three either side are shown — see the constant.
        const presence =
          gameCount < CAROUSEL_COMPACT_BELOW_GAMES
            ? Math.min(
                1,
                Math.max(
                  0,
                  (CAROUSEL_COMPACT_VISIBLE_STEPS + CAROUSEL_COMPACT_FADE_STEPS - distance) /
                    CAROUSEL_COMPACT_FADE_STEPS,
                ),
              )
            : 1;
        el.style.setProperty('--presence', String(presence));
        el.style.pointerEvents = presence > 0 ? '' : 'none';
        // The card's border takes the coin colour in step with how near the centre it is, as on mobile.
        el.style.setProperty('--focus', String(Math.max(0, 1 - distance)));
        // Nearer the centre stacks higher, so the focal card overlaps both its neighbours.
        el.style.zIndex = String(Math.max(0, 1000 - Math.round(distance * 100)));
      }

      const count = measuredCount.current;
      if (!byPlayer && count >= CAROUSEL_AUTO_LOAD_MAX_GAMES) return;

      if (
        isNearEnd(nearestIndex % count, count, CAROUSEL_LOAD_AHEAD_CARDS) ||
        needsNextPage({
          travelled: travelled.current,
          stride: stride.current,
          gameCount: count,
          loadAhead: CAROUSEL_LOAD_AHEAD_CARDS,
        })
      ) {
        loadMore();
      }
    },
    [gameCount, loadMore],
  );

  // Wrapping has to happen on every scroll, not only the animated ones: a swipe that carries past
  // the seam under its own momentum never goes through the rAF tick.
  const wrapScroll = useCallback(() => {
    const $el = carouselRef.current;
    const width = copyWidth.current;
    if (!$el || width <= 0) return 0;

    const shift = $el.scrollLeft >= 2 * width ? -width : $el.scrollLeft <= 0 ? width : 0;
    if (shift) {
      scrollTo($el.scrollLeft + shift);
    }

    return shift;
  }, [scrollTo]);

  useLayoutEffect(() => {
    const $el = carouselRef.current;
    const $track = trackRef.current;
    if (!$el || !$track || !gameCount) return undefined;

    const measure = () => {
      const $cards = Array.from($el.querySelectorAll<HTMLElement>('[data-carousel-item]'));
      if (!$cards[0]?.offsetWidth) return;

      // Overlap each card by a share of ITS OWN width — a category mixes tile widths (a long tile
      // is half again as wide as a regular one), so a single offset would overlap the narrow ones
      // far more than the design does. Applied before the track is measured, since these margins
      // are what set its width, and therefore the wrap point. Only written when it actually
      // changes: this runs from a ResizeObserver, and the write resizes the track.
      for (const el of $cards) {
        // Measured off the card rather than the wrapper: the card's own computed width is the
        // authoritative tile size, and it stays right even if the wrapper is laid out oddly.
        const $card = el.firstElementChild;
        const cardWidth = $card ? parseFloat(getComputedStyle($card).width) : el.offsetWidth;
        if (!cardWidth) continue;

        const margin = `${-(1 - CAROUSEL_STRIDE_RATIO) * cardWidth}px`;
        if (el.style.marginRight !== margin) el.style.marginRight = margin;
      }

      const width = $track.offsetWidth;
      if (width <= 0) return;

      copyWidth.current = width;
      // The scale curve is defined per card-step, so with mixed tile widths the step is the mean
      // one. Exact for a uniform row, and a close enough yardstick for a mixed one.
      stride.current = width / gameCount;
      items.current = $cards.map((el) => ({ el, centre: el.offsetLeft + el.offsetWidth / 2 }));

      // Enough copies that the [0, 2W) wrap band always has runway behind it.
      setCopies(Math.max(MIN_CAROUSEL_COPIES, Math.ceil($el.clientWidth / width) + 2));

      // Park one copy in so the row can be swiped backwards from the moment it renders, settled on
      // a card rather than mid-gap so the focal treatment reads from the very first frame.
      //
      // Latched rather than tested against scrollLeft: the park lands on the nearest card, which is
      // as often short of `width` as past it, so any "have we parked yet" reading of scrollLeft is
      // still true afterwards. `measure` re-runs on every ResizeObserver fire — a thumbnail
      // settling, a window resize, a mobile URL bar collapsing — and would yank a row that has been
      // playing for seconds back to the start.
      //
      // Only once the row can scroll: while it still renders the minimum copies a short category has
      // nothing to park on, and the pass that follows `setCopies` has to be the one that parks it.
      if (!hasParked.current && $el.scrollWidth > $el.clientWidth) {
        hasParked.current = true;

        const viewportCentre = width + $el.clientWidth / 2;
        const nearest = items.current.reduce((a, b) =>
          Math.abs(b.centre - viewportCentre) < Math.abs(a.centre - viewportCentre) ? b : a,
        );
        scrollTo(nearest.centre - $el.clientWidth / 2);
      }

      // A page landed. Its games sit behind the seam, so every copy after the first has moved right
      // by the width they added; putting the card the player was looking at back where it was
      // makes that invisible. Only for a change in the list: the same relayout after a thumbnail
      // settling or a resize is not a shift this can undo.
      if (measuredCount.current && measuredCount.current !== gameCount) {
        const { el, delta } = anchor.current;
        if (el?.isConnected) scrollTo(el.offsetLeft + el.offsetWidth / 2 - delta - $el.clientWidth / 2);
        travelled.current = 0;
        lastCentre.current = null;
      }
      measuredCount.current = gameCount;

      offset.current = $el.scrollLeft;

      paint();
    };

    measure();

    // Cards settle as their thumbnails load, so the first measurement is rarely the final one.
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe($el);
    resizeObserver.observe($track);

    return () => resizeObserver.disconnect();
  }, [copies, gameCount, paint, scrollTo]);

  // `aria-hidden` keeps the repeats out of the accessibility tree but leaves their links in the tab
  // order — focusable nodes inside a hidden subtree, which is the one combination the spec forbids.
  // Tabbing the row should walk the games once, so only the real copy is tabbable. Set on the DOM
  // rather than through a prop: the repeats have to stay clickable (they ARE the ones on screen),
  // so this is about focus alone, and GameCard does not own a tabIndex for React to fight over.
  //
  // Deliberately every render: `subCategoryGames` is an observable array this component re-renders
  // for without its identity necessarily changing, and a card that slipped through would be an
  // invisible tab stop.
  useEffect(() => {
    const $el = carouselRef.current;
    if (!$el) return;

    for (const $link of $el.querySelectorAll<HTMLElement>('[data-carousel-clone] a, [data-carousel-clone] button')) {
      $link.tabIndex = -1;
    }
  });

  // Rows are mounted lazily and then never unmounted, so a carousel the player scrolled past keeps
  // running unless something tells it not to.
  useEffect(() => {
    const $el = carouselRef.current;
    if (!$el || !gameCount) return undefined;

    const visibility = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      rootMargin: '200px 0px',
    });
    visibility.observe($el);

    return () => visibility.disconnect();
  }, [gameCount]);

  useEffect(() => {
    const $el = carouselRef.current;
    if (!$el || !gameCount || !inView) return undefined;

    // A player who asked the OS for less motion, a player being walked through the onboarding, and
    // a player who has tabbed into the row all get a row that only ever moves when they move it.
    //
    // NOTE: this does not on its own satisfy WCAG 2.2.2, which wants a persistent control any
    // player can operate, not just these three. That needs a visible pause affordance the design
    // does not have yet — raised on the PR.
    const holdDrift = prefersReducedMotion() || onboardingRow || focusWithin;

    let frame = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const elapsed = now - last;
      last = now;
      frame = requestAnimationFrame(tick);

      // An arrow nudge always runs to completion — that is the player driving the row, so it wins
      // over both the pause it set and the drift.
      if (step.current) {
        const { from, to, startedAt, duration } = step.current;
        const progress = Math.min(1, (now - startedAt) / duration);
        scrollTo(from + (to - from) * easeInOutCubic(progress));
        paint(true);

        if (progress >= 1) {
          step.current = null;
          // Only ever cross the seam between steps, so a wrap can never land mid-interpolation.
          wrapScroll();
          offset.current = $el.scrollLeft;
          paint(true);
        }
        return;
      }

      if (holdDrift || now < pausedUntil.current) return;

      // A backgrounded tab hands back one enormous delta on return; skipping a frame is cheaper
      // than teleporting the row halfway down the list.
      if (elapsed > 100) return;

      // Drifts left to right: content moves rightwards, so scrollLeft decreases.
      offset.current -= (stride.current * elapsed) / CAROUSEL_CARD_TRAVEL_MS;
      scrollTo(offset.current);
      // A wrap moves the row by a whole copy; the accumulator has to follow it.
      offset.current += wrapScroll();
      paint();
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      step.current = null;
    };
  }, [focusWithin, gameCount, inView, onboardingRow, paint, scrollTo, wrapScroll]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useCallback(
    throttle(() => {
      seenGamesLobbyListener();
    }, 100),
    [],
  );

  const handleScroll = useCallback(() => {
    // Consumed here whatever happens: the next event has to be judged on its own.
    const drivenByUs = selfScroll.current;
    selfScroll.current = false;

    // Whoever wrote it — the drift, a step, the opening park — has already wrapped and painted.
    if (drivenByUs) return;

    // A step already wraps and paints on its own tick; wrapping here would shift scrollLeft out
    // from under the interpolation and jump the row.
    if (!step.current) {
      wrapScroll();
      // The player is driving; the drift picks up from wherever they leave it.
      offset.current = carouselRef.current?.scrollLeft ?? offset.current;
      // Momentum scrolling and the held-drift path never reach the rAF tick.
      paint(true);
    }

    // Something other than the row moved it. A gesture, but also a programmatic
    // `card.scrollIntoView()` — `useScrollToGame` and the two missions progress bars all call it —
    // or a keyboard scroll, or the scrollbar. None of those pass through `handleGesture`, and a
    // smooth scrollIntoView in particular is fought frame by frame by the drift overwriting
    // scrollLeft, so it never arrives and the game it was bringing into view drifts straight back
    // out. Whoever moved the row gets it for CAROUSEL_RESUME_DELAY_MS.
    pauseAutoScroll();
    // And only this counts as the player looking at something: the carousel moving itself must
    // never reach the lobby-wide impression debounce — see `scrollTo`.
    onScroll();
  }, [onScroll, paint, pauseAutoScroll, wrapScroll]);

  if (!category || !gameCount) return null;

  const rowName = category.name[DEFAULT_LANGUAGE];

  return (
    <div className={classNames(carouselStyles.wrapper, carouselClassName)}>
      {/*
        Arrows are a desktop affordance; touch surfaces swipe instead (PLT-795). That gate lives in
        `.arrow` itself — `display: none` until the viewport is wide and tall enough for the
        `--sides-width` gutter they sit in — so it is left to CSS here, exactly as the standard
        carousel row leaves it. A second gate in JS only disagreed with the first.
      */}
      <button
        aria-label={t('common.previous')}
        className={classNames(carouselStyles.arrow, carouselStyles.arrowLeft, carouselLeftArrowClassName)}
        onClick={() => nudge(-1)}
      >
        <Chevron direction="left" />
      </button>

      <div
        ref={carouselRef}
        className={styles.carousel}
        // focusout carries the element focus is moving TO, so tabbing between cards inside the row
        // does not read as leaving it.
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocusWithin(false);
        }}
        onFocus={(event) => {
          if (isKeyboardFocus(event.target)) setFocusWithin(true);
        }}
        onPointerDown={(event) => {
          // A mouse press can't move the row — drag is off and the wheel doesn't pass through here —
          // so it must not park the drift. Touch and pen are real swipes.
          if (event.pointerType !== 'mouse') handleGesture();
        }}
        onScroll={handleScroll}
        onTouchStart={handleGesture}
      >
        {Array.from({ length: copies }, (_, copy) => {
          // PARKED_COPY is the real list as far as anything outside the paint is concerned; the
          // others repeat it to close the loop and are hidden from screen readers and the tab
          // order. The impression listener counts whichever instance the player can see, once,
          // so it does not consult this.
          const isClone = copy !== PARKED_COPY;
          const onboardingCard = onboardingRow && !isClone;
          return (
            <div
              key={copy}
              ref={isClone ? undefined : trackRef}
              aria-hidden={isClone || undefined}
              className={styles.track}
              data-carousel-clone={isClone || undefined}
            >
              {games.map((game, gameIdx) => (
                <div
                  key={`${game.identifier}:${game.masterCasinoGameId}`}
                  className={classNames(styles.item, coinTypeStore.coinType === 'SC' && styles.itemSc)}
                  // The game's position in the category, which its index among the row's cards is
                  // not: every copy repeats the same games. Analytics reads it for the horizontal
                  // location of an impression and of a launch.
                  data-carousel-item={gameIdx}
                >
                  <GameCard
                    carousel
                    animated={false}
                    duplicate={isClone}
                    game={game}
                    gameLabelFF={gameLabelFF}
                    onboarding={onboardingCard && gameIdx === 1}
                    rowName={rowName}
                    tooltipRef={onboardingCard && gameIdx === 1 ? setReferenceElement : null}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <span aria-hidden className={styles.edgeFade} />

      <button
        aria-label={t('common.next')}
        className={classNames(carouselStyles.arrow, carouselStyles.arrowRight, carouselRightArrowClassName)}
        onClick={() => nudge(1)}
      >
        <Chevron direction="right" />
      </button>
    </div>
  );
});
